import { useEffect, useRef, useState } from 'react';

import { useGeoMap } from '../GeoMap';

type UseProjectionChangeOptions = {
  /** Min rotation change (deg) to treat as a real move */
  rotEpsDeg?: number;
  /** Min scale change to treat as a real zoom */
  scaleEps?: number;
  /** Debounce before resetting to false after end */
  endDebounceMs?: number;
};

export const useProjectionChangingWithThreshold = (
  opts: UseProjectionChangeOptions = {},
) => {
  const { onChangeStart, onChangeProgress, onChangeEnd } = useGeoMap();

  const rotEps = opts.rotEpsDeg ?? 0.35;
  const scaleEps = opts.scaleEps ?? 0.75;
  const endDebounceMs = opts.endDebounceMs ?? 100;

  const [isProjectionChanging, setIsProjectionChanging] = useState(false);

  const startRotRef = useRef<[number, number, number] | null>(null);
  const startScaleRef = useRef<number | null>(null);
  const movedRef = useRef(false);
  const endResetTimer = useRef<number | null>(null);

  useEffect(
    () =>
      // cleanup on unmount
      () => {
        if (endResetTimer.current) {
          clearTimeout(endResetTimer.current);
          endResetTimer.current = null;
        }
      },
    [],
  );

  useEffect(() => {
    const offStart = onChangeStart(e => {
      const ev = (e as CustomEvent).detail;
      startRotRef.current = [...ev.rotation] as [number, number, number];
      startScaleRef.current = ev.scale;
      movedRef.current = false;

      // cancel pending end debounce if any
      if (endResetTimer.current) {
        clearTimeout(endResetTimer.current);
        endResetTimer.current = null;
      }
    });

    const offProgress = onChangeProgress(e => {
      if (movedRef.current) {
        return;
      }

      const ev = (e as CustomEvent).detail;
      const [λ0, φ0, γ0] = startRotRef.current ?? ev.rotation;
      const s0 = startScaleRef.current ?? ev.scale;

      const [λ, φ, γ] = ev.rotation;
      const s = ev.scale;

      const dRot = Math.max(
        Math.abs(λ - λ0),
        Math.abs(φ - φ0),
        Math.abs(γ - γ0),
      );
      const dScale = Math.abs(s - s0);

      if (dRot > rotEps || dScale > scaleEps) {
        movedRef.current = true;
        setIsProjectionChanging(true);
      }
    });

    const offEnd = onChangeEnd(() => {
      // if we never crossed the threshold, ensure false and exit
      if (!movedRef.current) {
        setIsProjectionChanging(false);
        return;
      }

      if (endResetTimer.current) {
        clearTimeout(endResetTimer.current);
      }
      endResetTimer.current = window.setTimeout(() => {
        setIsProjectionChanging(false);
        endResetTimer.current = null;
      }, endDebounceMs);
    });

    return () => {
      offStart();
      offProgress();
      offEnd();
    };
  }, [
    onChangeStart,
    onChangeProgress,
    onChangeEnd,
    rotEps,
    scaleEps,
    endDebounceMs,
  ]);

  return isProjectionChanging;
};
